import { api } from "@/lib/api";
import { SenderIdentitySelect } from "@/components/SenderIdentitySelect";
import {
  Button,
  Input,
  Label,
  Select,
  Textarea,
} from "@a7y/tailwind-catalyst";
import * as Headless from "@headlessui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { EmailPreview } from "./components/EmailPreview";
import { TestSendDialog } from "./components/TestSendDialog";
import { ConfirmSendDialog } from "./components/ConfirmSendDialog";
import type {
  ProductEmailInputMode,
  ProductEmailRecipientMode,
  ProductEmailSenderIdentity,
  ProductEmailSend,
} from "./types";

interface ComposeTabProps {
  initialDraft?: ProductEmailSend | null;
  onDraftCleared?: () => void;
}

export function ComposeTab({ initialDraft, onDraftCleared }: ComposeTabProps) {
  const [subject, setSubject] = useState(initialDraft?.subject ?? "");
  const [bodyContent, setBodyContent] = useState(
    initialDraft?.bodyContent ?? ""
  );
  const [inputMode, setInputMode] = useState<ProductEmailInputMode>(
    initialDraft?.inputMode ?? "plaintext"
  );
  const [senderIdentity, setSenderIdentity] =
    useState<ProductEmailSenderIdentity>(
      initialDraft?.senderIdentity ?? "team"
    );
  const [recipientMode, setRecipientMode] =
    useState<ProductEmailRecipientMode>(
      initialDraft?.recipientConfig?.mode ?? "registered"
    );
  const [manualEmails, setManualEmails] = useState(
    initialDraft?.recipientConfig?.manualEmails?.join(", ") ?? ""
  );
  const [previewHtml, setPreviewHtml] = useState("");
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
  const [draftId, setDraftId] = useState<string | null>(
    initialDraft?.id ?? null
  );
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"edit" | "preview">("edit");

  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // No prop→state sync effect: ProductEmailPage renders ComposeTab only while
  // the compose tab is active, and handleLoadDraft switches to that tab in the
  // same update that sets the draft — so a loaded draft always arrives on a
  // fresh mount and the useState initializers above already read it.

  // Recipient count for non-manual modes
  const { data: recipientCountData } = useQuery({
    queryKey: ["product-email-recipient-count", recipientMode],
    queryFn: () => api.getRecipientCount(recipientMode),
    enabled: recipientMode !== "manual_list",
  });

  // Compute manual email count client-side
  const manualEmailCount = manualEmails
    .split(/[,\n]/)
    .map((e) => e.trim())
    .filter((e) => e.includes("@")).length;

  const recipientCount =
    recipientMode === "manual_list"
      ? manualEmailCount
      : (recipientCountData?.count ?? 0);

  // Preview debouncing
  const fetchPreview = useCallback(() => {
    if (!bodyContent.trim()) {
      setPreviewHtml("");
      return;
    }
    api
      .previewProductEmail({ bodyContent, inputMode, senderIdentity, subject })
      .then((res) => setPreviewHtml(res.html))
      .catch(() => {
        // Silently fail preview — user will see empty preview
      });
  }, [bodyContent, inputMode, senderIdentity, subject]);

  useEffect(() => {
    clearTimeout(previewTimeoutRef.current);
    previewTimeoutRef.current = setTimeout(fetchPreview, 300);
    return () => clearTimeout(previewTimeoutRef.current);
  }, [fetchPreview]);

  // Save draft mutation
  const saveDraftMutation = useMutation({
    mutationFn: () =>
      api.saveDraft({
        id: draftId ?? undefined,
        subject,
        bodyContent,
        inputMode,
        senderIdentity,
        recipientConfig: {
          mode: recipientMode,
          manualEmails:
            recipientMode === "manual_list"
              ? manualEmails
                  .split(/[,\n]/)
                  .map((e) => e.trim())
                  .filter((e) => e.includes("@"))
              : undefined,
        },
      }),
    onSuccess: (data) => {
      setDraftId(data.draft.id);
    },
  });

  // Execute send mutation
  const executeSendMutation = useMutation({
    mutationFn: async () => {
      // Save draft first if not saved yet
      let id = draftId;
      if (!id) {
        const draft = await api.saveDraft({
          subject,
          bodyContent,
          inputMode,
          senderIdentity,
          recipientConfig: {
            mode: recipientMode,
            manualEmails:
              recipientMode === "manual_list"
                ? manualEmails
                    .split(/[,\n]/)
                    .map((e) => e.trim())
                    .filter((e) => e.includes("@"))
                : undefined,
          },
        });
        id = draft.draft.id;
      }
      return api.executeSend(id);
    },
    onSuccess: () => {
      // Reset form after successful send
      setSubject("");
      setBodyContent("");
      setInputMode("plaintext");
      setSenderIdentity("team");
      setRecipientMode("registered");
      setManualEmails("");
      setPreviewHtml("");
      setDraftId(null);
      setIsConfirmDialogOpen(false);
      onDraftCleared?.();
    },
  });

  const handleNewDraft = () => {
    setSubject("");
    setBodyContent("");
    setInputMode("plaintext");
    setSenderIdentity("team");
      setRecipientMode("registered");
    setManualEmails("");
    setPreviewHtml("");
    setDraftId(null);
    onDraftCleared?.();
  };

  const canSend =
    subject.trim().length > 0 &&
    bodyContent.trim().length > 0 &&
    recipientCount > 0;

  return (
    <div className="space-y-6">
      {draftId && (
        <div className="flex justify-end">
          <Button plain onClick={handleNewDraft}>
            New Draft
          </Button>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-x-6 gap-y-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        {/* Left column: Sender, Recipients, Actions */}
        <div className="space-y-5">
          <SenderIdentitySelect
            value={senderIdentity}
            onChange={(v) =>
              setSenderIdentity(v as ProductEmailSenderIdentity)
            }
          />

          <div className="space-y-3">
            <Headless.Field>
              <Label>Recipients</Label>
              <Select
                className="mt-2"
                value={recipientMode}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setRecipientMode(
                    e.target.value as ProductEmailRecipientMode
                  )
                }
              >
                <option value="registered">Registered Users</option>
                <option value="invited">Invited Users</option>
                <option value="free_trial">Free Trial Users</option>
                <option value="manual_list">Manual List</option>
              </Select>
            </Headless.Field>

            {recipientMode === "manual_list" && (
              <Headless.Field>
                <Label>Email Addresses (comma or newline separated)</Label>
                <Textarea
                  className="mt-2"
                  rows={4}
                  value={manualEmails}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setManualEmails(e.target.value)
                  }
                  placeholder="user1@example.com, user2@example.com"
                />
              </Headless.Field>
            )}

            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {recipientCount} {recipientCount === 1 ? "recipient" : "recipients"}
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 border-t border-zinc-200 pt-4 dark:border-zinc-700">
            <Button
              color="blue"
              disabled={!canSend || executeSendMutation.isPending}
              onClick={() => setIsConfirmDialogOpen(true)}
            >
              Send Email
            </Button>
            <Button
              outline
              onClick={() => setIsTestDialogOpen(true)}
              disabled={!subject.trim() || !bodyContent.trim()}
            >
              Send Test
            </Button>
            <Button
              plain
              onClick={() => saveDraftMutation.mutate()}
              disabled={
                !subject.trim() ||
                !bodyContent.trim() ||
                saveDraftMutation.isPending
              }
            >
              {saveDraftMutation.isPending ? "Saving..." : "Save Draft"}
            </Button>
          </div>

          {saveDraftMutation.isSuccess && (
            <span className="text-sm text-green-600 dark:text-green-400">
              Draft saved
            </span>
          )}
          {saveDraftMutation.isError && (
            <span className="text-sm text-red-600 dark:text-red-400">
              {saveDraftMutation.error?.message || "Failed to save draft"}
            </span>
          )}
          {executeSendMutation.isError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {executeSendMutation.error?.message || "Failed to send email"}
            </div>
          )}
          {executeSendMutation.isSuccess && (
            <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-400">
              Email sent successfully!
            </div>
          )}
        </div>

        {/* Right column: Subject, Body/Preview */}
        <div className="space-y-5">
          <Headless.Field>
            <Label>Subject</Label>
            <Input
              className="mt-2"
              value={subject}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSubject(e.target.value)
              }
              placeholder="Email subject line"
            />
          </Headless.Field>

          <Headless.Field>
            <Label>Body Content</Label>
            <Select
              className="mt-2"
              value={inputMode}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setInputMode(e.target.value as ProductEmailInputMode)
              }
            >
              <option value="plaintext">Plain Text</option>
              <option value="html">HTML</option>
              <option value="markdown">Markdown</option>
            </Select>

            {/* Edit / Preview segmented control */}
            <div className="mt-3 flex gap-1 rounded-lg border border-zinc-200 p-0.5 dark:border-zinc-700 w-fit">
              <button
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  editorMode === "edit"
                    ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
                }`}
                onClick={() => setEditorMode("edit")}
              >
                Edit
              </button>
              <button
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  editorMode === "preview"
                    ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
                }`}
                onClick={() => setEditorMode("preview")}
              >
                Preview
              </button>
            </div>

            {editorMode === "edit" ? (
              <Textarea
                className="mt-2"
                rows={14}
                value={bodyContent}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setBodyContent(e.target.value)
                }
                placeholder={
                  inputMode === "html"
                    ? "<p>Your HTML content here...</p>"
                    : inputMode === "markdown"
                      ? "# Your Markdown content here..."
                      : "Your email content here..."
                }
              />
            ) : (
              <div className="mt-2 rounded-xl border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
                <div className="flex items-center justify-end border-b border-zinc-200 px-4 py-2 dark:border-zinc-700">
                  <div className="flex gap-1 rounded-lg border border-zinc-200 p-0.5 dark:border-zinc-700">
                    <button
                      className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                        viewMode === "desktop"
                          ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                          : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
                      }`}
                      onClick={() => setViewMode("desktop")}
                    >
                      Desktop
                    </button>
                    <button
                      className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                        viewMode === "mobile"
                          ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                          : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
                      }`}
                      onClick={() => setViewMode("mobile")}
                    >
                      Mobile
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <EmailPreview html={previewHtml} viewMode={viewMode} />
                </div>
              </div>
            )}
          </Headless.Field>
        </div>
      </div>

      {/* Dialogs */}
      <TestSendDialog
        open={isTestDialogOpen}
        onClose={() => setIsTestDialogOpen(false)}
        subject={subject}
        bodyContent={bodyContent}
        inputMode={inputMode}
        senderIdentity={senderIdentity}
      />

      <ConfirmSendDialog
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={() => executeSendMutation.mutate()}
        isPending={executeSendMutation.isPending}
        subject={subject}
        senderIdentity={senderIdentity}
        recipientCount={recipientCount}
      />
    </div>
  );
}
